<script setup lang="ts">
import PasswordInput from '../components/PasswordInput.vue'
import PasswordChecklist from '../components/PasswordChecklist.vue'
import PasswordMatchHint from '../components/PasswordMatchHint.vue'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '../composables/useAuth'
import { useUserStore } from '../stores/user'
import { changeMyPassword } from '../services/api'
import { meetsPasswordPolicy } from '../utils/passwordPolicy'

const router = useRouter()
const { getAccessTokenSilently } = useAuth()
const userStore = useUserStore()

const form = ref({ newPassword: '', confirmPassword: '' })
const saving = ref(false)
const error = ref('')
const canSubmit = computed(() =>
    meetsPasswordPolicy(form.value.newPassword) && form.value.newPassword === form.value.confirmPassword
)

async function submit() {
    error.value = ''
    if (!canSubmit.value || saving.value) return

    saving.value = true
    try {
        const token = await getAccessTokenSilently()
        await changeMyPassword(form.value.newPassword, token)
        if (userStore.user) {
            userStore.user.mustChangePassword = false
        }
        router.push(userStore.getOnboardingRoute())
    } catch (e: unknown) {
        const err = e as { response?: { data?: { error?: string } } }
        error.value = err.response?.data?.error || 'Failed to change password'
    } finally {
        saving.value = false
    }
}
</script>

<template>
    <div class="min-h-screen bg-cc-deep flex items-center justify-center px-4">
        <div class="w-full max-w-md">
            <div class="bg-cc-elevated rounded-cc border border-cc-border p-8 shadow-lg">
                <div class="text-center mb-8">
                    <div class="w-14 h-14 mx-auto mb-4 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                        <svg class="w-7 h-7 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                        </svg>
                    </div>
                    <h1 class="text-2xl font-bold text-cc-text">Set Your Password</h1>
                    <p class="text-cc-text-secondary text-sm mt-2">
                        Your account was created with a temporary password. Please set a new one to continue.
                    </p>
                </div>

                <div v-if="error" class="mb-4 p-3 bg-cc-danger/10 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                    {{ error }}
                </div>

                <div class="space-y-4">
                    <div>
                        <label for="new-password" class="block text-sm font-medium text-cc-text-secondary mb-1">New Password</label>
                        <PasswordInput
                            id="new-password"
                            v-model="form.newPassword"
                            autocomplete="new-password"
                            aria-describedby="new-password-rules"
                            class="block w-full rounded-lg border border-cc-border-med bg-cc-deep text-cc-text shadow-sm px-4 py-2.5 focus:ring-2 focus:ring-cc-primary focus:border-cc-primary focus:outline-none"
                            @keyup.enter="submit"
                        />
                        <PasswordChecklist id="new-password-rules" :password="form.newPassword" />
                    </div>
                    <div>
                        <label for="confirm-password" class="block text-sm font-medium text-cc-text-secondary mb-1">Confirm Password</label>
                        <PasswordInput
                            id="confirm-password"
                            v-model="form.confirmPassword"
                            autocomplete="new-password"
                            aria-describedby="confirm-password-match"
                            placeholder="Re-enter your new password"
                            class="block w-full rounded-lg border border-cc-border-med bg-cc-deep text-cc-text shadow-sm px-4 py-2.5 focus:ring-2 focus:ring-cc-primary focus:border-cc-primary focus:outline-none"
                            @keyup.enter="submit"
                        />
                        <PasswordMatchHint id="confirm-password-match" :password="form.newPassword" :confirm="form.confirmPassword" />
                    </div>
                </div>

                <button
                    @click="submit"
                    :disabled="saving || !canSubmit"
                    class="w-full mt-6 bg-cc-primary text-white px-6 py-2.5 rounded-lg font-bold hover:bg-cc-primary-hover disabled:opacity-50 flex items-center justify-center gap-2"
                >
                    <span v-if="saving" class="animate-spin">↻</span>
                    {{ saving ? 'Setting Password...' : 'Set Password & Continue' }}
                </button>
            </div>
        </div>
    </div>
</template>
